import { useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'
import {
  getComparisonTargets,
  groupEducationInstitutionsByLocation,
  calculateEducationLocationStats,
  createPublicPrivateRatioComparison,
  createEducationalEquityBubbleChart,
  calculateInfrastructureLocationStats,
  createInfrastructureCompletenessRankingOption,
  createResourceAdequacyComparisonOption,
  createWASHComplianceComparisonOption,
  createTeacherWorkforceQualityOption,
} from '../../utils/educationComparisonUtils'

function EducationComparison({
  facilities,
  locations,
  selectedDistrict,
  selectedSubcounty,
  selectedParish,
  selectedVillage,
  isViewingAllDistricts,
  districtFacilityGroups,
  sortBy,
  sortOrder,
  showTopN,
  maxLocationDepth,
}) {
  // Determine what locations to compare
  const comparisonConfig = useMemo(() => {
    if (isViewingAllDistricts() && districtFacilityGroups) {
      // Multi-district mode: compare districts
      const districtTargets = Object.keys(districtFacilityGroups).map(
        districtName => ({
          id: districtName.toLowerCase(),
          name: districtName,
          code: `UG.${districtName.toUpperCase().slice(0, 3)}`,
        })
      )

      return {
        targets: districtTargets,
        type: 'districts',
        context: 'Comparing education across all districts',
        level: 'district',
      }
    }

    return {
      ...getComparisonTargets(
        locations,
        selectedDistrict,
        selectedSubcounty,
        selectedParish,
        selectedVillage,
        maxLocationDepth
      ),
      context: 'Comparing education facilities',
    }
  }, [
    locations,
    selectedDistrict,
    selectedSubcounty,
    selectedParish,
    selectedVillage,
    isViewingAllDistricts,
    districtFacilityGroups,
    maxLocationDepth,
  ])

  // Group education institutions by comparison targets and calculate stats
  const locationStats = useMemo(() => {
    if (!comparisonConfig.targets.length || !facilities.length) return []

    if (isViewingAllDistricts() && districtFacilityGroups) {
      // Custom grouping for district comparison
      const districtTargets = comparisonConfig.targets.map(target => ({
        id: target.id,
        name: target.name,
      }))

      const grouped = groupEducationInstitutionsByLocation(
        facilities,
        districtTargets,
        'district'
      )
      return calculateEducationLocationStats(grouped)
    }

    const grouped = groupEducationInstitutionsByLocation(
      facilities,
      comparisonConfig.targets,
      comparisonConfig.level
    )
    return calculateEducationLocationStats(grouped)
  }, [
    facilities,
    comparisonConfig,
    isViewingAllDistricts,
    districtFacilityGroups,
  ])

  // Calculate infrastructure-specific location stats
  const infrastructureLocationStats = useMemo(() => {
    if (!comparisonConfig.targets.length || !facilities.length) return []

    if (isViewingAllDistricts() && districtFacilityGroups) {
      // Custom grouping for district comparison
      const districtTargets = comparisonConfig.targets.map(target => ({
        id: target.id,
        name: target.name,
      }))

      const grouped = groupEducationInstitutionsByLocation(
        facilities,
        districtTargets,
        'district'
      )
      return calculateInfrastructureLocationStats(grouped)
    }

    const grouped = groupEducationInstitutionsByLocation(
      facilities,
      comparisonConfig.targets,
      comparisonConfig.level
    )
    return calculateInfrastructureLocationStats(grouped)
  }, [
    facilities,
    comparisonConfig,
    isViewingAllDistricts,
    districtFacilityGroups,
  ])

  // Generate rankings with current sort settings (default to qualityScore for primary schools)
  const rankings = useMemo(() => {
    if (!locationStats.length) return []

    const sortedStats = [...locationStats].sort((a, b) => {
      // Default to qualityScore if no specific sort field is set or if sortBy is 'compositeScore'
      const effectiveSortBy =
        sortBy === 'compositeScore' || !sortBy ? 'qualityScore' : sortBy
      const aVal = a[effectiveSortBy] || 0
      const bVal = b[effectiveSortBy] || 0
      return sortOrder === 'desc' ? bVal - aVal : aVal - bVal
    })

    return sortedStats.slice(0, showTopN).map((stat, index) => ({
      ...stat,
      rank: index + 1,
      percentileRank: Math.round((index / sortedStats.length) * 100),
    }))
  }, [locationStats, sortBy, sortOrder, showTopN])

  const publicPrivateRatioOption = useMemo(() => {
    if (!locationStats.length) return null
    return createPublicPrivateRatioComparison(
      locationStats,
      'Public vs Private Education Distribution'
    )
  }, [locationStats])

  const educationalEquityBubbleOption = useMemo(() => {
    if (!locationStats.length) return null
    return createEducationalEquityBubbleChart(
      locationStats,
      'Educational Equity and Coverage Analysis'
    )
  }, [locationStats])

  // Infrastructure comparison chart options
  const infrastructureCompletenessRankingOption = useMemo(() => {
    if (!infrastructureLocationStats.length) return null
    return createInfrastructureCompletenessRankingOption(
      infrastructureLocationStats,
      'Infrastructure Completeness Ranking'
    )
  }, [infrastructureLocationStats])

  const resourceAdequacyComparisonOption = useMemo(() => {
    if (!infrastructureLocationStats.length) return null
    return createResourceAdequacyComparisonOption(
      infrastructureLocationStats,
      'Resource Adequacy Comparison'
    )
  }, [infrastructureLocationStats])

  const washComplianceComparisonOption = useMemo(() => {
    if (!infrastructureLocationStats.length) return null
    return createWASHComplianceComparisonOption(
      infrastructureLocationStats,
      'WASH Compliance Comparison'
    )
  }, [infrastructureLocationStats])

  const teacherWorkforceQualityOption = useMemo(() => {
    if (!infrastructureLocationStats.length) return null
    return createTeacherWorkforceQualityOption(
      infrastructureLocationStats,
      'Teacher Workforce Quality'
    )
  }, [infrastructureLocationStats])

  // Check if we have sufficient data for comparison
  const hasLocationComparison = locationStats.length > 1
  // const totalInstitutions = locationStats.reduce(
  //   (sum, stat) => sum + stat.totalInstitutions,
  //   0
  // )

  // If we're in facility mode or have very limited comparison data, show facility-level info
  if (
    comparisonConfig.type === 'facilities' ||
    (!hasLocationComparison && facilities.length > 0)
  ) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="text-center py-8">
          <div className="text-6xl mb-4">🏫</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Individual Education Institutions
          </h3>
          <p className="text-gray-600 mb-4">
            {comparisonConfig.type === 'facilities'
              ? `Showing ${facilities.length} institutions in ${comparisonConfig.context}`
              : `Found ${facilities.length} institutions but insufficient location data for comparison`}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-md mx-auto">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {
                  facilities.filter(f => f.institution_type === 'Primary')
                    .length
                }
              </div>
              <div className="text-sm text-blue-800">Primary Schools</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {
                  facilities.filter(f => f.institution_type === 'Secondary')
                    .length
                }
              </div>
              <div className="text-sm text-green-800">Secondary Schools</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {facilities.filter(f => f.ownership === 'Government').length}
              </div>
              <div className="text-sm text-yellow-800">Government Schools</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Public vs Private Ratio Comparison */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          Public vs Private Education Distribution
        </h3>
        <div className="h-80">
          {publicPrivateRatioOption && locationStats.length > 0 ? (
            <ReactECharts
              option={publicPrivateRatioOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              theme="ug-dashboard"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <div className="text-4xl mb-2">📊</div>
                <p>Insufficient data for ownership ratio comparison</p>
                <p className="text-sm mt-2">
                  Need institutions across multiple {comparisonConfig.level}s
                </p>
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p className="font-medium">
            What is the balance between public and private education?
          </p>
          <p className="mt-2">
            Comparison showing the split between government and private
            institutions across locations.
          </p>
        </div>
      </div>

      {/* Educational Equity and Coverage Analysis */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          Educational Equity and Coverage Analysis
        </h3>
        <div className="h-80">
          {educationalEquityBubbleOption && locationStats.length > 0 ? (
            <ReactECharts
              option={educationalEquityBubbleOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              theme="ug-dashboard"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <div className="text-4xl mb-2">📊</div>
                <p>Insufficient data for equity analysis</p>
                <p className="text-sm mt-2">
                  Need institutions across multiple {comparisonConfig.level}s
                </p>
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p className="font-medium">
            Which areas have the most equitable education access?
          </p>
          <p className="mt-2">
            Bubble chart showing institutional count, government percentage, and
            parish coverage. Bubble size indicates geographic spread of
            education services.
          </p>
        </div>
      </div>

      {/* Infrastructure Completeness Ranking */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          Infrastructure Completeness Ranking
        </h3>
        <div className="h-80">
          {infrastructureCompletenessRankingOption &&
          infrastructureLocationStats.length > 0 ? (
            <ReactECharts
              option={infrastructureCompletenessRankingOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              theme="ug-dashboard"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <div className="text-4xl mb-2">🏗️</div>
                <p>
                  Insufficient data for infrastructure completeness comparison
                </p>
                <p className="text-sm mt-2">
                  Need institutions with infrastructure data across multiple{' '}
                  {comparisonConfig.level}s
                </p>
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p className="font-medium">
            Which areas have the most complete educational infrastructure?
          </p>
          <p className="mt-2">
            Infrastructure completeness score based on water, electricity, ICT,
            toilets, disability access, and permanent classrooms.
          </p>
        </div>
      </div>

      {/* Resource Adequacy Comparison */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          Resource Adequacy Comparison
        </h3>
        <div className="h-80">
          {resourceAdequacyComparisonOption &&
          infrastructureLocationStats.length > 0 ? (
            <ReactECharts
              option={resourceAdequacyComparisonOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              theme="ug-dashboard"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <div className="text-4xl mb-2">⚖️</div>
                <p>Insufficient data for resource adequacy comparison</p>
                <p className="text-sm mt-2">
                  Need institutions with resource ratio data across multiple{' '}
                  {comparisonConfig.level}s
                </p>
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p className="font-medium">
            Which locations have the most strained resources?
          </p>
          <p className="mt-2">
            Comparison of average pupil-teacher, pupil-classroom, and
            pupil-toilet ratios with benchmark lines showing recommended
            standards.
          </p>
        </div>
      </div>

      {/* WASH Compliance Comparison */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          WASH Compliance Comparison
        </h3>
        <div className="h-80">
          {washComplianceComparisonOption &&
          infrastructureLocationStats.length > 0 ? (
            <ReactECharts
              option={washComplianceComparisonOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              theme="ug-dashboard"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <div className="text-4xl mb-2">🚿</div>
                <p>Insufficient data for WASH compliance comparison</p>
                <p className="text-sm mt-2">
                  Need institutions with WASH facility data across multiple{' '}
                  {comparisonConfig.level}s
                </p>
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p className="font-medium">
            Where are learning environments safest and most hygienic?
          </p>
          <p className="mt-2">
            Percentage of schools with water, handwashing facilities, and
            gender-separated toilets by location.
          </p>
        </div>
      </div>

      {/* Teacher Workforce Quality */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          Teacher Workforce Quality
        </h3>
        <div className="h-80">
          {teacherWorkforceQualityOption &&
          infrastructureLocationStats.length > 0 ? (
            <ReactECharts
              option={teacherWorkforceQualityOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              theme="ug-dashboard"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <div className="text-4xl mb-2">👩‍🏫</div>
                <p>
                  Insufficient data for teacher workforce quality comparison
                </p>
                <p className="text-sm mt-2">
                  Need institutions with teacher qualification data across
                  multiple {comparisonConfig.level}s
                </p>
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p className="font-medium">
            Which areas have the most qualified teaching workforce?
          </p>
          <p className="mt-2">
            Stacked bar chart showing percentage of teachers with Certificate,
            Diploma, and Degree qualifications by location.
          </p>
        </div>
      </div>

      {/* Primary School Quality Index */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          🏫 Primary School Quality Index
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left text-sm font-medium text-gray-700 p-3">
                  Rank
                </th>
                <th className="text-left text-sm font-medium text-gray-700 p-3">
                  Location
                </th>
                <th className="text-center text-sm font-medium text-gray-700 p-3">
                  Schools
                </th>
                <th className="text-center text-sm font-medium text-gray-700 p-3">
                  Students
                </th>
                <th className="text-center text-sm font-medium text-gray-700 p-3">
                  Avg S:T Ratio
                </th>
                <th className="text-center text-sm font-medium text-gray-700 p-3">
                  Infrastructure %
                </th>
                <th className="text-center text-sm font-medium text-gray-700 p-3">
                  Quality Score
                </th>
              </tr>
            </thead>
            <tbody>
              {rankings.map((location, index) => (
                <tr
                  key={location.id}
                  className={`border-b ${index < 3 ? 'bg-blue-50' : ''}`}
                >
                  <td className="p-3">
                    <div className="flex items-center">
                      <span
                        className={`text-sm font-bold ${
                          location.rank === 1
                            ? 'text-yellow-600'
                            : location.rank === 2
                              ? 'text-gray-600'
                              : location.rank === 3
                                ? 'text-orange-600'
                                : 'text-gray-800'
                        }`}
                      >
                        #{location.rank}
                      </span>
                      {location.rank <= 3 && (
                        <span className="ml-1 text-lg">
                          {location.rank === 1
                            ? '🥇'
                            : location.rank === 2
                              ? '🥈'
                              : '🥉'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="font-medium text-gray-900">
                      {location.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {location.percentileRank}th percentile
                    </div>
                  </td>
                  <td className="text-center p-3">
                    <div className="text-lg font-semibold text-blue-600">
                      {location.totalInstitutions}
                    </div>
                  </td>
                  <td className="text-center p-3">
                    <div className="text-sm font-medium text-gray-900">
                      {location.totalStudents
                        ? location.totalStudents.toLocaleString()
                        : '0'}
                    </div>
                  </td>
                  <td className="text-center p-3">
                    <div
                      className={`text-sm font-semibold ${
                        location.avgStudentTeacherRatio <= 40
                          ? 'text-green-600'
                          : location.avgStudentTeacherRatio <= 50
                            ? 'text-yellow-600'
                            : 'text-red-600'
                      }`}
                    >
                      {location.avgStudentTeacherRatio
                        ? `${Math.round(location.avgStudentTeacherRatio)}:1`
                        : 'N/A'}
                    </div>
                  </td>
                  <td className="text-center p-3">
                    <div
                      className={`text-sm font-semibold ${
                        location.infrastructurePercentage >= 60
                          ? 'text-green-600'
                          : location.infrastructurePercentage >= 30
                            ? 'text-yellow-600'
                            : 'text-red-600'
                      }`}
                    >
                      {location.infrastructurePercentage}%
                    </div>
                  </td>
                  <td className="text-center p-3">
                    <div
                      className={`text-lg font-semibold ${
                        location.qualityScore >= 75
                          ? 'text-green-600'
                          : location.qualityScore >= 50
                            ? 'text-yellow-600'
                            : 'text-red-600'
                      }`}
                    >
                      {location.qualityScore}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p className="font-medium">
            Primary school quality assessment by location
          </p>
          <p className="mt-2">
            Quality Score combines student enrollment scale (30%), resource
            adequacy (25%), infrastructure readiness (25%), and government
            support (20%).
          </p>
          <p className="mt-1">
            Infrastructure % = schools with electricity + water + basic
            facilities.
          </p>
        </div>
      </div>

      {/* Educational Insights Summary */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          🎓 Education Summary
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600">
              {locationStats.reduce(
                (sum, stat) => sum + stat.totalInstitutions,
                0
              )}
            </div>
            <div className="text-sm text-gray-600">Total Institutions</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {locationStats.reduce((sum, stat) => sum + stat.primaryCount, 0)}
            </div>
            <div className="text-sm text-gray-600">Primary Schools</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {locationStats.reduce(
                (sum, stat) => sum + stat.secondaryCount,
                0
              )}
            </div>
            <div className="text-sm text-gray-600">Secondary Schools</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {Math.round(
                locationStats.reduce(
                  (sum, stat) => sum + stat.governmentRatio,
                  0
                ) / Math.max(locationStats.length, 1)
              )}
              %
            </div>
            <div className="text-sm text-gray-600">Avg Gov Ratio</div>
          </div>
        </div>
      </div>
    </>
  )
}

EducationComparison.propTypes = {
  facilities: PropTypes.arrayOf(PropTypes.object).isRequired,
  locations: PropTypes.object.isRequired,
  selectedDistrict: PropTypes.string,
  selectedSubcounty: PropTypes.string,
  selectedParish: PropTypes.string,
  selectedVillage: PropTypes.string,
  isViewingAllDistricts: PropTypes.func.isRequired,
  districtFacilityGroups: PropTypes.object,
  sortBy: PropTypes.string.isRequired,
  sortOrder: PropTypes.string.isRequired,
  showTopN: PropTypes.number.isRequired,
  maxLocationDepth: PropTypes.string.isRequired,
}

export default EducationComparison
